import type { HelperMutationRecord } from './helperOutboxDb'
import type { ChecklistBlockedTodoPolicy } from '../../utils/checklistBlockedTodoPolicy'
import type { HelperVisibilityMask } from './visibilityMask'
import type { OpsPhoneItemKey } from '../../../functions-common/src/ops/phoneItems'
import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  ACTIVE_WORKSPACE_TAB_STORAGE_KEY,
} from '../../context/workspaceContextCore'
import {
  requestWorkspaceSwitch,
  type WorkspaceSwitchSource,
} from '../workspaceSwitchService'

export type HelperSyncState = 'IDLE' | 'SYNCING' | 'ERROR' | 'OFFLINE'
export type HelperOperationPhase = 'QUEUED' | 'SYNCING' | 'UPLOADING' | 'FINALIZING'

export interface HelperWorkspaceSummary {
  id: string
  name: string
  avatarUrl?: string | null
  color?: string | null
  role?: string | null
  joinedAt?: string | null
  helperAppLocale?: string | null
  cardBackgroundsEnabled?: boolean
  checklistBlockedTodoPolicy?: ChecklistBlockedTodoPolicy
  branding?: { logoUrl: string | null; accentColor: string | null }
  helperVisibilityMaskDefault?: HelperVisibilityMask | null
  appAccess?: boolean
  notifyVia?: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY'
}

export type HelperTabKey = 'TASKS' | 'CALENDAR' | 'MEETINGS' | 'CHAT' | 'FIELD' | 'SCHOOL' | 'OPS'

export type EduHelperJob = 'ROLL' | 'GATE' | 'BUS' | 'DESK'

export interface HelperStoreSnapshot {
  syncState: HelperSyncState
  pending: HelperMutationRecord[]
  failed: HelperMutationRecord[]
  recentCompleted: Array<HelperMutationRecord & { completedAt: number }>
  operationPhases: Record<string, HelperOperationPhase>
  lastSyncAt: number | null
  lastError: string | null
  workspaces: HelperWorkspaceSummary[]
  activeWorkspaceId: string | null
  workspacesLoading: boolean
  unreadNotificationCount: number
  /** Last time the helper viewed each tab (ms epoch). Persisted. */
  lastSeenByTab: Record<HelperTabKey, number>
  eduJobs: EduHelperJob[]
  eduJobsByWorkspace: Record<string, EduHelperJob[]>
  eduRouteIds: string[]
  eduRollSessionIds: string[]
  opsPhoneItems: OpsPhoneItemKey[]
  opsPhoneItemsByWorkspace: Record<string, OpsPhoneItemKey[]>
}

type Listener = (snapshot: HelperStoreSnapshot) => void

const LEGACY_ACTIVE_WORKSPACE_STORAGE_KEY = 'helper.activeWorkspaceId'
const TAB_SEEN_STORAGE_KEY = 'helper.lastSeenByTab'

export const migrateLegacyHelperActiveWorkspaceKey = (): void => {
  if (typeof window === 'undefined') return
  try {
    const legacy = window.localStorage.getItem(LEGACY_ACTIVE_WORKSPACE_STORAGE_KEY)
    if (legacy !== null) {
      if (!window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)) {
        window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, legacy)
      }
      window.localStorage.removeItem(LEGACY_ACTIVE_WORKSPACE_STORAGE_KEY)
    }
  } catch {
    // ignore
  }
}

migrateLegacyHelperActiveWorkspaceKey()

export const readPersistedActiveWorkspaceId = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const tabKey = window.sessionStorage.getItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
    if (tabKey) return tabKey
    return window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
  } catch {
    return null
  }
}

const EMPTY_TAB_SEEN: Record<HelperTabKey, number> = {
  TASKS: 0,
  CALENDAR: 0,
  MEETINGS: 0,
  CHAT: 0,
  FIELD: 0,
  SCHOOL: 0,
  OPS: 0,
}

const readPersistedTabSeen = (): Record<HelperTabKey, number> => {
  if (typeof window === 'undefined') return { ...EMPTY_TAB_SEEN }
  try {
    const raw = window.localStorage.getItem(TAB_SEEN_STORAGE_KEY)
    if (!raw) return { ...EMPTY_TAB_SEEN }
    const parsed = JSON.parse(raw) as Partial<Record<HelperTabKey, number>> & {
      NEXUS?: number
      SCAN?: number
      CONTROLS?: number
    }
    const legacyNexusSeen = typeof parsed.NEXUS === 'number' ? parsed.NEXUS : 0
    return {
      TASKS: typeof parsed.TASKS === 'number' ? parsed.TASKS : 0,
      CALENDAR: typeof parsed.CALENDAR === 'number' ? parsed.CALENDAR : legacyNexusSeen,
      MEETINGS: typeof parsed.MEETINGS === 'number' ? parsed.MEETINGS : legacyNexusSeen,
      CHAT: typeof parsed.CHAT === 'number' ? parsed.CHAT : legacyNexusSeen,
      FIELD: typeof parsed.FIELD === 'number' ? parsed.FIELD : typeof parsed.CONTROLS === 'number' ? parsed.CONTROLS : typeof parsed.SCAN === 'number' ? parsed.SCAN : 0,
      SCHOOL: typeof parsed.SCHOOL === 'number' ? parsed.SCHOOL : 0,
      OPS: typeof parsed.OPS === 'number' ? parsed.OPS : 0,
    }
  } catch {
    return { ...EMPTY_TAB_SEEN }
  }
}

const persistTabSeen = (state: Record<HelperTabKey, number>): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(TAB_SEEN_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

let snapshot: HelperStoreSnapshot = {
  syncState: typeof navigator !== 'undefined' && !navigator.onLine ? 'OFFLINE' : 'IDLE',
  pending: [],
  failed: [],
  recentCompleted: [],
  operationPhases: {},
  lastSyncAt: null,
  lastError: null,
  workspaces: [],
  activeWorkspaceId: readPersistedActiveWorkspaceId(),
  workspacesLoading: true,
  unreadNotificationCount: 0,
  lastSeenByTab: readPersistedTabSeen(),
  eduJobs: [],
  eduJobsByWorkspace: {},
  eduRouteIds: [],
  eduRollSessionIds: [],
  opsPhoneItems: [],
  opsPhoneItemsByWorkspace: {},
}

const listeners = new Set<Listener>()

const emit = () => {
  for (const listener of listeners) listener(snapshot)
}

export const getHelperSnapshot = (): HelperStoreSnapshot => snapshot

export const subscribeHelperStore = (listener: Listener): (() => void) => {
  listeners.add(listener)
  listener(snapshot)
  return () => {
    listeners.delete(listener)
  }
}

export const setHelperState = (patch: Partial<HelperStoreSnapshot>): void => {
  snapshot = { ...snapshot, ...patch }
  emit()
}

export const setHelperQueue = (records: HelperMutationRecord[]): void => {
  const pending = records.filter((entry) => entry.status === 'PENDING' || entry.status === 'SYNCING')
  const failed = records.filter((entry) => entry.status === 'FAILED' || entry.status === 'ACCESS_LOST')
  snapshot = { ...snapshot, pending, failed }
  emit()
}

export const setHelperOperationPhase = (id: string, phase: HelperOperationPhase | null): void => {
  const operationPhases = { ...snapshot.operationPhases }
  if (phase) operationPhases[id] = phase
  else delete operationPhases[id]
  snapshot = { ...snapshot, operationPhases }
  emit()
}

export const recordHelperCompletedMutation = (record: HelperMutationRecord): void => {
  const completedAt = Date.now()
  const completedRecord: HelperMutationRecord & { completedAt: number } = {
    ...record,
    status: 'COMPLETED',
    completedAt,
  }
  snapshot = {
    ...snapshot,
    recentCompleted: [
      completedRecord,
      ...snapshot.recentCompleted.filter((entry) => entry.id !== record.id && completedAt - entry.completedAt < 5 * 60_000),
    ].slice(0, 8),
  }
  emit()
}

type HelperWorkspaceSwitchCallback = (workspaceId: string) => void
let registeredWorkspaceSwitchCallback: HelperWorkspaceSwitchCallback | null = null
let pendingWorkspaceSwitchId: string | null = null
let pinnedWorkspaceId: string | null = null

export const getPinnedHelperWorkspaceId = (): string | null => pinnedWorkspaceId

export const pinHelperWorkspaceForSharedDevice = (workspaceId: string | null): void => {
  pinnedWorkspaceId = workspaceId
  if (workspaceId !== null) {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, workspaceId)
      } catch {
        // ignore
      }
    }
    syncHelperActiveWorkspace(workspaceId)
  } else {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
        window.sessionStorage.removeItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
        window.localStorage.removeItem(LEGACY_ACTIVE_WORKSPACE_STORAGE_KEY)
      } catch {
        // ignore
      }
    }
    syncHelperActiveWorkspace(null)
  }
}

export const registerHelperWorkspaceSwitchCallback = (
  cb: HelperWorkspaceSwitchCallback | null,
): (() => void) => {
  registeredWorkspaceSwitchCallback = cb
  if (cb && pendingWorkspaceSwitchId) {
    const targetId = pinnedWorkspaceId ?? pendingWorkspaceSwitchId
    pendingWorkspaceSwitchId = null
    cb(targetId)
  }
  return () => {
    if (registeredWorkspaceSwitchCallback === cb) {
      registeredWorkspaceSwitchCallback = null
    }
  }
}

export const syncHelperActiveWorkspace = (id: string | null): void => {
  const targetId = pinnedWorkspaceId ?? id
  if (pendingWorkspaceSwitchId && targetId && snapshot.workspaces.some((entry) => entry.id === targetId)) {
    pendingWorkspaceSwitchId = null
  }
  if (targetId === snapshot.activeWorkspaceId) return
  const cachedJobs = targetId && snapshot.eduJobsByWorkspace ? (snapshot.eduJobsByWorkspace[targetId] ?? []) : []
  snapshot = { ...snapshot, activeWorkspaceId: targetId, eduJobs: cachedJobs }
  emit()
  if (targetId) {
    void refreshHelperEduJobs(targetId)
  }
}

export const setHelperWorkspaces = (
  workspaces: HelperWorkspaceSummary[],
  options: { loading?: boolean } = {},
): void => {
  const loading = options.loading ?? false
  snapshot = {
    ...snapshot,
    workspaces,
    workspacesLoading: loading,
  }
  emit()

  if (pinnedWorkspaceId) {
    pendingWorkspaceSwitchId = null
    return
  }

  if (workspaces.length > 0 && (!snapshot.activeWorkspaceId || !workspaces.some((entry) => entry.id === snapshot.activeWorkspaceId))) {
    const targetId = workspaces[0].id
    if (registeredWorkspaceSwitchCallback) {
      pendingWorkspaceSwitchId = null
      registeredWorkspaceSwitchCallback(targetId)
    } else {
      pendingWorkspaceSwitchId = targetId
    }
  } else {
    pendingWorkspaceSwitchId = null
  }
}

export const setActiveHelperWorkspaceId = (id: string | null): void => {
  const resolvedId = pinnedWorkspaceId ?? id
  if (registeredWorkspaceSwitchCallback && resolvedId !== null) {
    registeredWorkspaceSwitchCallback(resolvedId)
    return
  }
  syncHelperActiveWorkspace(resolvedId)
}

export const setHelperEduJobsForWorkspace = (
  workspaceId: string,
  jobs: EduHelperJob[],
  routeIds: string[] = [],
  rollSessionIds: string[] = [],
): void => {
  const nextByWorkspace = {
    ...snapshot.eduJobsByWorkspace,
    [workspaceId]: jobs,
  }
  const isActive = snapshot.activeWorkspaceId === workspaceId
  snapshot = {
    ...snapshot,
    eduJobsByWorkspace: nextByWorkspace,
    eduJobs: isActive ? jobs : snapshot.eduJobs,
    eduRouteIds: isActive ? routeIds : snapshot.eduRouteIds,
    eduRollSessionIds: isActive ? rollSessionIds : snapshot.eduRollSessionIds,
  }
  emit()
}

export const getHelperEduJobs = (workspaceId?: string | null): EduHelperJob[] => {
  const wsId = workspaceId ?? snapshot.activeWorkspaceId
  if (!wsId) return snapshot.eduJobs ?? []
  return snapshot.eduJobsByWorkspace[wsId] ?? (wsId === snapshot.activeWorkspaceId ? snapshot.eduJobs : [])
}

export const setHelperOpsPhoneItemsForWorkspace = (
  workspaceId: string,
  items: OpsPhoneItemKey[],
): void => {
  const current = snapshot.opsPhoneItemsByWorkspace[workspaceId]
  if (
    current &&
    current.length === items.length &&
    current.every((item, idx) => item === items[idx])
  ) {
    return
  }
  const nextByWorkspace = {
    ...snapshot.opsPhoneItemsByWorkspace,
    [workspaceId]: items,
  }
  const isActive = snapshot.activeWorkspaceId === workspaceId
  snapshot = {
    ...snapshot,
    opsPhoneItemsByWorkspace: nextByWorkspace,
    opsPhoneItems: isActive ? items : snapshot.opsPhoneItems,
  }
  emit()
}

export const clearHelperOpsPhoneItems = (workspaceId?: string): void => {
  if (workspaceId) {
    const next = { ...snapshot.opsPhoneItemsByWorkspace }
    delete next[workspaceId]
    snapshot = {
      ...snapshot,
      opsPhoneItemsByWorkspace: next,
      opsPhoneItems: snapshot.activeWorkspaceId === workspaceId ? [] : snapshot.opsPhoneItems,
    }
  } else {
    snapshot = {
      ...snapshot,
      opsPhoneItemsByWorkspace: {},
      opsPhoneItems: [],
    }
  }
  emit()
}

export const getHelperOpsPhoneItems = (workspaceId?: string | null): OpsPhoneItemKey[] => {
  const wsId = workspaceId ?? snapshot.activeWorkspaceId
  if (!wsId) return snapshot.opsPhoneItems ?? []
  return snapshot.opsPhoneItemsByWorkspace[wsId] ?? (wsId === snapshot.activeWorkspaceId ? snapshot.opsPhoneItems : [])
}

export const refreshHelperEduJobs = async (workspaceId?: string | null): Promise<EduHelperJob[]> => {
  const wsId = workspaceId ?? snapshot.activeWorkspaceId
  if (!wsId) return []
  try {
    const { getMyEduJobs } = await import('../eduHelperJobsService')
    const result = await getMyEduJobs({ workspaceId: wsId })
    const jobs = (result.eduJobs ?? []) as EduHelperJob[]
    const routeIds = result.eduRouteIds ?? []
    const rollSessionIds = result.eduRollSessionIds ?? []
    setHelperEduJobsForWorkspace(wsId, jobs, routeIds, rollSessionIds)
    return jobs
  } catch {
    return snapshot.eduJobsByWorkspace[wsId] ?? []
  }
}

export const requestHelperWorkspaceSwitch = async (
  targetId: string | null,
  source: WorkspaceSwitchSource = 'USER',
  landing?: string | null,
): Promise<boolean> => {
  const result = await requestWorkspaceSwitch({
    toWorkspaceId: targetId,
    source,
    landing,
  })
  return result === 'SWITCHED' || result === 'NOOP'
}

export const setHelperUnreadNotifications = (count: number): void => {
  if (count === snapshot.unreadNotificationCount) return
  snapshot = { ...snapshot, unreadNotificationCount: count }
  emit()
}

export const getActiveHelperWorkspace = (): HelperWorkspaceSummary | null => {
  const id = snapshot.activeWorkspaceId
  if (!id) return null
  return snapshot.workspaces.find((entry) => entry.id === id) ?? null
}

export const markHelperTabSeen = (tab: HelperTabKey): void => {
  const now = Date.now()
  const next: Record<HelperTabKey, number> = { ...snapshot.lastSeenByTab, [tab]: now }
  snapshot = { ...snapshot, lastSeenByTab: next }
  persistTabSeen(next)
  emit()
}
